import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import RouteHelpers from 'helpers/routes';
import { toastError } from 'components/Toast';
import useCurrentUser from 'hooks/useCurrentUser';
import VideoResource from '../VideoResource';
import PlaylistItem from '../PlaylistItem';
import { Pagination } from 'components/Organization/Pagination';
import EmptyState from 'pages/LearningResources/EmptyState';
import { FilterState } from 'pages/LearningResources/FilterSideBar/FilterSideBar';
import { RecommendationMetadata } from 'types/api/ClassScoresRecommendation';

import {
  ResourceType,
  LearningResourceSearchResult
} from 'types/api/LearningResource';

import {
  getHoursMinutes,
  getResourceRoute
} from 'pages/LearningResources/utils';

import {
  useLearningResources,
  getLrTitle,
  LrAction
} from './useLearningResources';

const DEFAULT_RESOURCES_PER_PAGE = 12;

interface ResourcesSectionProps {
  action: LrAction;
  educatorId?: number;
  educatorGuid?: string;
  educatorName?: string;
  showTitle?: boolean;
  filters?: FilterState;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onRecommendation?: (metadata: RecommendationMetadata | null) => void;

  /** Controlled current page (1-based). Falls back to internal state when omitted. */
  page?: number;

  /** Called when the page changes; enables controlled pagination. */
  onPageChange?: (page: number) => void;

  /** Number of resources to display per page. */
  itemsPerPage?: number;
}

function ResourcesSection({
  action,
  educatorId,
  educatorGuid,
  educatorName,
  showTitle = true,
  filters,
  sortBy,
  sortDir,
  onRecommendation,
  page: controlledPage,
  onPageChange,
  itemsPerPage = DEFAULT_RESOURCES_PER_PAGE
}: ResourcesSectionProps) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [internalPage, setInternalPage] = useState(1);
  const currentPage = controlledPage ?? internalPage;

  // Reset internal page when query criteria changes (uncontrolled mode only)
  const criteriaKey = useMemo(
    () =>
      JSON.stringify({
        action,
        filters,
        sortBy,
        sortDir,
        educatorId,
        educatorGuid
      }),
    [action, filters, sortBy, sortDir, educatorId, educatorGuid]
  );
  useEffect(() => {
    if (controlledPage === undefined) setInternalPage(1);
  }, [criteriaKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnabled =
    (action !== 'completedRecommended' ||
      (educatorId !== undefined && educatorId !== 0)) &&
    (action !== 'recommendedResources' ||
      (educatorGuid !== undefined && educatorGuid !== ''));

  const { data, isPending, error } = useLearningResources({
    action,
    filters,
    sortBy,
    sortDir,
    page: currentPage,
    perPage: itemsPerPage,
    educatorId,
    educatorGuid,
    enabled: isEnabled
  });

  useEffect(() => {
    if (error) toastError({ description: (error as Error).message });
  }, [error]);

  useEffect(() => {
    if (action === 'classroomScores' && data !== undefined) {
      onRecommendation?.(data.recommendation);
    }
  }, [data, action, onRecommendation]);

  /* Coach can also be an educator.
   * If the user is a coach and the current path is coach-learning-resources
   * then keep navigation as coach. */
  const isCoachLoggedInAsCoach = useCallback(() => {
    return (
      user.roles.includes('lr_coach') &&
      location.pathname.includes(
        RouteHelpers.getPath('coach-learning-resources')
      )
    );
  }, [location.pathname, user.roles]);

  const handleClick = useCallback(
    (id: number, resourceType: ResourceType) =>
      navigate(getResourceRoute(id, resourceType, isCoachLoggedInAsCoach())),
    [isCoachLoggedInAsCoach, navigate]
  );

  function handlePageChange(newPage: number) {
    if (newPage === currentPage) return;
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  }

  const title = getLrTitle(action, educatorName);
  const resources = data?.resources ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="mb-10">
      {showTitle && (
        <div className="block md:flex items-center mb-2">
          <h2 className="text-black">{title}</h2>
        </div>
      )}

      {isPending ? (
        <div className="mb-10">
          <Loader active inline="centered" />
        </div>
      ) : (
        <>
          {resources.length === 0 ? (
            <div
              className="w-full flex justify-center items-center my-12"
              aria-live="polite"
            >
              <EmptyState />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 my-4 sm:grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3">
              {resources.map(
                (video: LearningResourceSearchResult, idx: number) =>
                  video.type === 'learning_resource' ? (
                    <VideoResource
                      key={`${video.type}-${video.id}`}
                      ariaLabel="Video Thumbnail"
                      ariaRoleDescription="Slide"
                      handleClick={handleClick}
                      video={{
                        name: video.name,
                        id: video.id ? video.id : idx,
                        videoURI: video.video_uri
                          ? video.video_uri
                          : '1_bkuk1xi7',
                        estimatedTime: video.estimated_time
                          ? getHoursMinutes(video.estimated_time)
                          : '0',
                        type: video.type,
                        resourceType: video.resource_type,
                        thumbnailUrl: video.thumbnail_url
                      }}
                      tags={video.tags}
                    />
                  ) : (
                    <PlaylistItem
                      key={`${video.type}-${video.id}`}
                      percentage={video.completion_percentage || 0}
                      numberOfResources={video.total_resources || 1}
                      handleClick={handleClick}
                      video={{
                        name: video.name,
                        id: video.id ? video.id : idx,
                        introImage: video.intro_image,
                        videoURI: video.video_uri
                          ? video.video_uri
                          : '1_bkuk1xi7',
                        type: video.type
                      }}
                    />
                  )
              )}
            </div>
          )}

          {resources.length > 0 && totalPages > 1 && (
            <div className="mt-6 mb-10 flex justify-center">
              <Pagination
                page={currentPage}
                onPageChange={handlePageChange}
                total={totalPages}
                ariaLabel={title}
                goTo={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResourcesSection;
